import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import AddBrandModal from '../AddBrandModal';
import Pagination from '../common/Pagination';
import Card from '../common/Card';
import Button from '../common/Button';
import PageHeader from '../common/PageHeader';
import EmptyState from '../common/EmptyState';
import { LoadingState } from '../common/Spinner';
import { Table, THead, Th, Tr, Td } from '../common/Table';
import ImportBrandsModal from '../ImportBrandsModal';
import DraftBlockerModal from '../modals/DraftBlockerModal';

// Import html2pdf dynamically
let html2pdf;
if (typeof window !== 'undefined') {
  html2pdf = require('html2pdf.js');
}

const StockCheckPage = ({ navigationParams, onNavigationComplete, onViewChange }) => {
  const { hasPermission, enableStockCheckBrandDeletion } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();

  // View state: 'list' or 'form'
  const [view, setView] = useState('list');
  const [isViewMode, setIsViewMode] = useState(false);
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const [checkDate, setCheckDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [missingCategoryIds, setMissingCategoryIds] = useState([]);
  const [previousRecordDate, setPreviousRecordDate] = useState(null);

  // Categories and records
  const [categories, setCategories] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Stock check form state
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [mlFilter, setMlFilter] = useState('');
  const [packageFilter, setPackageFilter] = useState('');
  const [showAddBrandModal, setShowAddBrandModal] = useState(false);
  const [showImportBrandsModal, setShowImportBrandsModal] = useState(false);
  
  // Current stock state: { CATEGORY_NAME: [{ id, name, size, ob, count, invoicePrice, mrpPrice, sellingPrice }] }
  const [currentStockState, setCurrentStockState] = useState({});
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [draftStatus, setDraftStatus] = useState('none');
  const [isTodayLocked, setIsTodayLocked] = useState(false);
  const [showDraftBlocker, setShowDraftBlocker] = useState(false);
  const [draftToBlock, setDraftToBlock] = useState(null);
  const lastSavedStateStrRef = React.useRef(null);
  const stockStateRef = React.useRef(currentStockState);

  useEffect(() => {
    stockStateRef.current = currentStockState;
  }, [currentStockState]);

  // Category order for display
  const categoryOrder = ['BRANDY', 'WHISKY', 'RUM', 'GIN', 'VODKA', 'LIQUEUR', 'RTD', 'TEQUILA', 'WINE', 'BEER', 'NON-ALCOHOL'];

  const handleDateChange = (newDate) => {
    const existingDates = records.map(r => r.check_date.split('T')[0]);
    existingDates.sort((a, b) => new Date(a) - new Date(b));
    
    if (existingDates.length > 0) {
      const latestDate = existingDates[existingDates.length - 1];
      const currentRecordDate = currentRecordId ? records.find(r => r.id === currentRecordId)?.check_date?.split('T')[0] : null;
      
      if (newDate < latestDate && newDate !== currentRecordDate) {
        toast.error("You cannot select a date between existing Stock Check entries. Please select a date after the last entry.");
        return;
      }
    }
    setCheckDate(newDate);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (view === 'list') {
      loadRecords();
      setCurrentPage(1); // Reset to first page when switching to list view
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Handle navigation parameters from dashboard
  useEffect(() => {
    if (navigationParams && view === 'list') {
      if (navigationParams.filterDate) {
        setCheckDate(navigationParams.filterDate);
      }
      if (navigationParams.recordId) {
        // Find and highlight the record
        setTimeout(() => {
          const recordElement = document.getElementById(`record-${navigationParams.recordId}`);
          if (recordElement) {
            recordElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            recordElement.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
            setTimeout(() => {
              recordElement.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
            }, 3000);
          }
          // Clear navigation params after processing
          if (onNavigationComplete) {
            setTimeout(() => onNavigationComplete(), 1000);
          }
        }, 500);
      } else if (onNavigationComplete) {
        // Clear params even if no recordId
        setTimeout(() => onNavigationComplete(), 500);
      }
    }
  }, [navigationParams, view, onNavigationComplete]);

  useEffect(() => {
    if (view === 'form' && categories.length > 0 && !activeCategory) {
      // Set first category as active
      const firstCategory = categories.find(c => categoryOrder.includes(c.name));
      if (firstCategory) {
        setActiveCategory(firstCategory.id);
      }
    }
  }, [view, categories]);

  const loadDraftOrTemplate = async (targetDate) => {
    if (!targetDate) return;
    
    // Check Today's Lock first
    const today = new Date().toISOString().split('T')[0];
    if (targetDate === today) {
       try {
          const checkRes = await api.get('/stock/check-records');
          const todayFinal = checkRes.data.records.find(r => r.check_date.startsWith(today) && r.status === 'Completed');
          if (todayFinal) {
             setIsTodayLocked(true);
             setDraftStatus('none');
             toast.info("Today's Stock Check has already been submitted successfully.");
             viewRecord(todayFinal.id);
             return;
          } else {
             setIsTodayLocked(false);
          }
       } catch (e) {
          console.error(e);
       }
    } else {
       setIsTodayLocked(false);
    }

    try {
      const draftRes = await api.get('/stock/drafts', { params: { date: targetDate } });
      if (draftRes.data.draft) {
         const draft = draftRes.data.draft;
         const normalized = normalizeStockData(draft.stock_data);
         setCurrentStockState(normalized);
         lastSavedStateStrRef.current = JSON.stringify(draft.stock_data);
         toast.success('Recovered your previous Draft.');
         setDraftStatus('saved');
         setLastSaveTime(new Date(draft.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
         return; 
      }
    } catch (error) {
      console.error('Error fetching draft:', error);
    }

    loadStockTemplate(targetDate);
  };

  useEffect(() => {
    if (view === 'form' && !isViewMode && !currentRecordId && categories.length > 0) {
      loadDraftOrTemplate(checkDate);
    }
  }, [view, isViewMode, currentRecordId, checkDate, categories]);

  // Auto-Save Draft Feature
  useEffect(() => {
    if (view !== 'form' || isViewMode || isDraftSaving || currentRecordId || isTodayLocked) return;
    
    const intervalId = setInterval(() => {
      autoSaveDraft(false);
    }, 30000); // 30 seconds
    
    return () => clearInterval(intervalId);
  }, [view, isViewMode, isDraftSaving, checkDate, currentRecordId, isTodayLocked]);

  // Notify parent when entering/exiting entry mode
  useEffect(() => {
    if (onViewChange) {
      onViewChange(view === 'form');
    }
  }, [view, onViewChange]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/stock/categories');
      // Filter to only the 10 categories we need, in the correct order
      const allCategories = response.data.categories || [];
      const orderedCategories = categoryOrder.map(name => 
        allCategories.find(c => c.name === name)
      ).filter(Boolean);
      setCategories(orderedCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRecords = async () => {
    try {
      setLoading(true);
      const response = await api.get('/stock/check-records');
      setRecords(response.data.records || []);
    } catch (error) {
      console.error('Error loading records:', error);
      setRecords([]);
      if (error.response) {
        if (error.response.status === 401) {
          toast.error('Your session has expired. Please log in again.');
        } else if (error.response.status === 403) {
          toast.error('You do not have permission to view stock check records.');
        } else if (error.response.data && error.response.data.error) {
          toast.error(`Error: ${error.response.data.error}`);
        } else {
          toast.error('Failed to load stock check records. Please try again.');
        }
      } else if (error.request) {
        toast.error('Network error: Could not connect to server. Please check your connection and try again.');
      } else {
        toast.error('Failed to load stock check records. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Normalize stock data to ensure consistent field names and calculations
  const normalizeStockData = (stockData) => {
    if (!stockData || typeof stockData !== 'object') {
      return {};
    }

    const normalized = {};

    Object.keys(stockData).forEach(categoryName => {
      const brands = Array.isArray(stockData[categoryName]) ? stockData[categoryName] : [];
      normalized[categoryName] = brands.map(brand => {
        // Only use 0 if the value is explicitly 0, otherwise use empty string for display
        const openingBalanceVal = brand.openingBalance ?? brand.opening_balance ?? brand.ob;
        const openingBalance = openingBalanceVal === 0 ? 0 : (openingBalanceVal || '');
        
        const purchaseVal = brand.purchase ?? brand.receipt;
        const purchase = purchaseVal === 0 ? 0 : (purchaseVal || '');
        
        const closingVal = brand.closingBalance ?? brand.closing_balance;
        const closingBalance = closingVal === 0 ? 0 : (closingVal ?? '');
        const salesVal = brand.sales;
        const sales = salesVal === 0 ? 0 : (salesVal || '');
        
        // Calculate totals using numeric values (treat empty as 0 for calculation)
        const openingNum = openingBalance === '' ? 0 : (typeof openingBalance === 'number' ? openingBalance : parseInt(openingBalance, 10) || 0);
        const purchaseNum = purchase === '' ? 0 : (typeof purchase === 'number' ? purchase : parseInt(purchase, 10) || 0);
        const total = openingNum + purchaseNum;

        // Prefer provided closing balance and derive sales from it; otherwise derive closing from sales
        let closingNum = closingBalance === '' ? null : (typeof closingBalance === 'number' ? closingBalance : parseInt(closingBalance, 10) || 0);
        let salesNum = sales === '' ? 0 : (typeof sales === 'number' ? sales : parseInt(sales, 10) || 0);

        if (closingNum !== null && !Number.isNaN(closingNum)) {
          closingNum = Math.max(0, closingNum);
          salesNum = Math.max(0, total - closingNum);
        } else {
          salesNum = Math.max(0, salesNum);
          closingNum = Math.max(0, total - salesNum);
        }

        const sellingPriceVal = brand.sellingPrice ?? brand.selling_price;
        const sellingPrice = sellingPriceVal === 0 ? 0 : (sellingPriceVal || '');

        const sellingNum = sellingPrice === '' ? 0 : (typeof sellingPrice === 'number' ? sellingPrice : parseFloat(sellingPrice) || 0);
        const salesIncome = parseFloat((salesNum * sellingNum).toFixed(2));

        const rawPackageType = brand.package_type ?? brand.packageType ?? '';
        const packageTypeVal = rawPackageType || (() => {
          const name = brand.name || '';
          const upperName = name.toUpperCase();
          if (upperName.endsWith(' - BTL')) return 'BTL';
          if (upperName.endsWith(' - TPACK')) return 'TPACK';
          if (upperName.endsWith(' - CAN')) return 'CAN';
          return '';
        })();

        return {
          id: brand.brandId ?? brand.brand_id ?? brand.id ?? null,
          brandId: brand.brandId ?? brand.brand_id ?? brand.id ?? null,
          categoryId: brand.categoryId ?? brand.category_id ?? null,
          name: brand.name || '',
          size: brand.size || '750ml',
          package_type: packageTypeVal,
          packageType: packageTypeVal,
          openingBalance,
          purchase,
          total,
          sales: salesNum,
          closingBalance: closingNum,
          invoicePrice: brand.invoicePrice ?? brand.invoice_price ?? '',
          mrpPrice: brand.mrpPrice ?? brand.mrp_price ?? '',
          sellingPrice,
          salesIncome
        };
      });
    });

    return normalized;
  };

  const loadStockTemplate = async (targetDate) => {
    if (!targetDate) return;

    try {
      setTemplateLoading(true);
      const response = await api.get('/stock/check-template', {
        params: { checkDate: targetDate }
      });

      const data = response.data || {};
      const normalizedStockData = normalizeStockData(data.stockData || {});
      const nextState = {};
      const categoryNames = categories.length > 0
        ? categories.map(cat => cat.name)
        : Object.keys(normalizedStockData);

      categoryNames.forEach(name => {
        nextState[name] = normalizedStockData[name] || [];
      });

      setCurrentStockState(nextState);
      setNeedsSetup(Boolean(data.needsSetup));
      setMissingCategoryIds(data.missingCategoryIds || []);
      setPreviousRecordDate(data.previousRecordDate || null);
    } catch (error) {
      console.error('Error loading stock template:', error);
      initializeStockState();
      setMissingCategoryIds([]);
      setNeedsSetup(false);
      setPreviousRecordDate(null);
      
      let errorMessage = 'Failed to load stock template. Please add brands manually.';
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = 'Your session has expired. Please log in again.';
        } else if (error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      toast.error(errorMessage);
    } finally {
      setTemplateLoading(false);
    }
  };

  const initializeStockState = () => {
    const state = {};
    categories.forEach(cat => {
      state[cat.name] = [];
    });
    setCurrentStockState(state);
  };

  const persistCategoryPreferences = async (categoryId, brandIds) => {
    await api.put(
      `/stock/brand-preferences/${categoryId}`,
      { brandIds }
    );
  };

  const createNewStockCheck = async () => {
    const today = new Date().toISOString().split('T')[0];
    
    try {
      const res = await api.get('/stock/drafts', { params: { date: today } });
      if (res.data.draft) {
         setDraftToBlock(res.data.draft);
         setShowDraftBlocker(true);
         return;
      }
    } catch (e) {
       console.error(e);
    }

    proceedCreateNew();
  };

  const proceedCreateNew = () => {
    const today = new Date().toISOString().split('T')[0];
    setCheckDate(today);
    initializeStockState();
    setIsViewMode(false);
    setCurrentRecordId(null);
    setNeedsSetup(false);
    setMissingCategoryIds([]);
    setPreviousRecordDate(null);
    setView('form');
    if (categories.length > 0) {
      const firstCategory = categories.find(c => categoryOrder.includes(c.name));
      if (firstCategory) {
        setActiveCategory(firstCategory.id);
      }
    }
  };

  const deleteBlockedDraft = async () => {
    if (draftToBlock) {
      try {
        await api.delete(`/stock/drafts/${draftToBlock.id}`);
        setShowDraftBlocker(false);
        setDraftToBlock(null);
        toast.success('Draft deleted successfully');
        proceedCreateNew();
      } catch (e) {
        toast.error('Failed to delete draft. You may not have permission.');
      }
    }
  };
  
  const continueBlockedDraft = () => {
     setShowDraftBlocker(false);
     setDraftToBlock(null);
     proceedCreateNew(); 
  };

  const showRecordsList = () => {
    setView('list');
    setIsViewMode(false);
    setCurrentRecordId(null);
    loadRecords();
  };

  const viewRecord = async (id) => {
    try {
      const response = await api.get(`/stock/check-records/${id}`);
      
      const record = response.data.record;
      if (!record) {
        toast.error('Record not found');
        return;
      }

      // Parse stock_data JSONB
      let stockData = {};
      if (record.stock_data) {
        try {
          stockData = typeof record.stock_data === 'string' 
            ? JSON.parse(record.stock_data) 
            : record.stock_data;
        } catch (error) {
          console.error('Error parsing stock_data:', error);
          stockData = {};
        }
      }
      
      // Ensure stockData is an object
      if (!stockData || typeof stockData !== 'object') {
        stockData = {};
      }

      // Normalize stock data to ensure consistent field names
      const normalizedStockData = normalizeStockData(stockData);
      setCurrentStockState(normalizedStockData);
      setIsViewMode(true);
      setCurrentRecordId(id);
      const recordDate = record.check_date || new Date().toISOString().split('T')[0];
      setCheckDate(recordDate);
      setNeedsSetup(false);
      setMissingCategoryIds([]);
      setPreviousRecordDate(recordDate);
      setView('form');
      
      // Set first category with data as active
      const firstCategoryWithData = categories.find(cat => 
        normalizedStockData[cat.name] && normalizedStockData[cat.name].length > 0
      );
      if (firstCategoryWithData) {
        setActiveCategory(firstCategoryWithData.id);
      } else if (categories.length > 0) {
        setActiveCategory(categories[0].id);
      }
    } catch (error) {
      console.error('Error loading record:', error);
      toast.error('Failed to load record');
    }
  };

  const editRecord = async (id) => {
    try {
      const response = await api.get(`/stock/check-records/${id}`);
      
      const record = response.data.record;
      if (!record) {
        toast.error('Record not found');
        return;
      }

      // Parse stock_data JSONB
      let stockData = {};
      if (record.stock_data) {
        try {
          stockData = typeof record.stock_data === 'string' 
            ? JSON.parse(record.stock_data) 
            : record.stock_data;
        } catch (error) {
          console.error('Error parsing stock_data:', error);
          stockData = {};
        }
      }

      // Ensure stockData is an object
      if (!stockData || typeof stockData !== 'object') {
        stockData = {};
      }
      
      // Normalize stock data to ensure consistent field names
      const normalizedStockData = normalizeStockData(stockData);
      setCurrentStockState(normalizedStockData);
      setIsViewMode(false);
      setCurrentRecordId(id);
      const recordDate = record.check_date || new Date().toISOString().split('T')[0];
      setCheckDate(recordDate);
      setNeedsSetup(false);
      setMissingCategoryIds([]);
      setPreviousRecordDate(recordDate);
      setView('form');
      
      // Set first category with data as active
      const firstCategoryWithData = categories.find(cat => 
        normalizedStockData[cat.name] && normalizedStockData[cat.name].length > 0
      );
      if (firstCategoryWithData) {
        setActiveCategory(firstCategoryWithData.id);
      } else if (categories.length > 0) {
        setActiveCategory(categories[0].id);
      }
    } catch (error) {
      console.error('Error loading record for edit:', error);
      toast.error('Failed to load record');
    }
  };

  const deleteRecord = async (id) => {
    const confirmed = await confirm({
      title: 'Delete this stock check record?',
      message: 'This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await api.delete(`/stock/check-records/${id}`);
      loadRecords();
    } catch (error) {
      console.error('Error deleting record:', error);
      let errorMessage = 'Failed to delete record';
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = 'Your session has expired. Please log in again.';
        } else if (error.response.status === 403) {
          errorMessage = 'You do not have permission to delete this record.';
        } else if (error.response.status === 404) {
          errorMessage = 'Record not found.';
        } else if (error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      }
      toast.error(errorMessage);
    }
  };

  const autoSaveDraft = async (isManual = false) => {
    if (!checkDate || currentRecordId) return;
    
    if (!navigator.onLine) {
      setDraftStatus('offline');
      return;
    }

    try {
      const stockData = {};
      let hasData = false;
      const stateToSave = stockStateRef.current;
      
      Object.keys(stateToSave).forEach(categoryName => {
        const brands = (stateToSave[categoryName] || []).filter(brand => brand.name && brand.name.trim());
        if (brands.length > 0) {
          hasData = true;
          stockData[categoryName] = brands.map(brand => {
            const opening = brand.openingBalance === '' || brand.openingBalance == null ? 0 : (typeof brand.openingBalance === 'number' ? brand.openingBalance : parseInt(brand.openingBalance, 10) || 0);
            const purchase = brand.purchase === '' || brand.purchase == null ? 0 : (typeof brand.purchase === 'number' ? brand.purchase : parseInt(brand.purchase, 10) || 0);
            const total = opening + purchase;
            const closingInput = brand.closingBalance === '' || brand.closingBalance == null ? null : (typeof brand.closingBalance === 'number' ? brand.closingBalance : parseInt(brand.closingBalance, 10) || 0);
            let sales = brand.sales === '' || brand.sales == null ? 0 : (typeof brand.sales === 'number' ? brand.sales : parseInt(brand.sales, 10) || 0);
            let closing = closingInput;
            if (closingInput !== null && !Number.isNaN(closingInput)) {
              closing = Math.max(0, closingInput);
              sales = Math.max(0, total - closing);
            } else {
              sales = Math.max(0, sales);
              closing = Math.max(0, total - sales);
            }
            const invoicePrice = brand.invoicePrice === '' || brand.invoicePrice == null ? 0 : (typeof brand.invoicePrice === 'number' ? brand.invoicePrice : parseFloat(brand.invoicePrice) || 0);
            const mrpPrice = brand.mrpPrice === '' || brand.mrpPrice == null ? 0 : (typeof brand.mrpPrice === 'number' ? brand.mrpPrice : parseFloat(brand.mrpPrice) || 0);
            const sellingPrice = brand.sellingPrice === '' || brand.sellingPrice == null ? 0 : (typeof brand.sellingPrice === 'number' ? brand.sellingPrice : parseFloat(brand.sellingPrice) || 0);
            const salesIncome = parseFloat(((sales || 0) * sellingPrice).toFixed(2));
            return {
              ...brand,
              openingBalance: opening,
              purchase,
              total,
              sales,
              closingBalance: closing,
              invoicePrice: parseFloat(invoicePrice.toFixed(2)),
              mrpPrice: parseFloat(mrpPrice.toFixed(2)),
              sellingPrice: parseFloat(sellingPrice.toFixed(2)),
              salesIncome
            };
          });
        }
      });
      if (!hasData) return;
      
      const payloadStr = JSON.stringify(stockData);
      if (!isManual && lastSavedStateStrRef.current === payloadStr) return;

      const payload = { check_date: checkDate, stock_data: stockData };
      setDraftStatus('saving');
      
      const res = await api.post('/stock/drafts', payload);
      
      lastSavedStateStrRef.current = payloadStr;
      setLastSaveTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      setDraftStatus('saved');
      
      if (isManual) {
        toast.success('Draft saved successfully');
      }
    } catch (error) {
      console.error('Auto-save draft failed:', error);
      setDraftStatus('offline');
      if (isManual) {
        toast.error('Failed to save draft. Check connection.');
      }
    }
  };

  const saveStockCheck = async () => {
    if (!checkDate) {
      toast.error('Please select a stock check date.');
      return;
    }

    // Validate date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(checkDate)) {
      toast.error('Please select a valid stock check date.');
      return;
    }

    // Validate date is not in the future
    const selectedDate = new Date(checkDate);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (selectedDate > today) {
      toast.error('Stock check date cannot be in the future.');
      return;
    }

    // Removed: incompleteCategories check to let user save without brand setup in all categories.

    // Check if there's any data to save
    const hasData = Object.keys(currentStockState).some(categoryName => {
      const brands = currentStockState[categoryName] || [];
      return brands.length > 0;
    });

    if (!hasData) {
      toast.error('Please add at least one brand before saving the stock check.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('You are not authenticated. Please log in again.');
        return;
      }

      const stockData = {};
      Object.keys(currentStockState).forEach(categoryName => {
        const category = categories.find(cat => cat.name === categoryName);
        const brands = (currentStockState[categoryName] || []).filter(
          brand => brand.name && brand.name.trim()
        );
        if (brands.length > 0) {
          stockData[categoryName] = brands.map(brand => {
            // Handle empty values - convert to 0 for storage but preserve empty for display
            const opening = brand.openingBalance === '' || brand.openingBalance === null || brand.openingBalance === undefined
              ? 0
              : (typeof brand.openingBalance === 'number' ? brand.openingBalance : parseInt(brand.openingBalance, 10) || 0);
            const purchase = brand.purchase === '' || brand.purchase === null || brand.purchase === undefined
              ? 0
              : (typeof brand.purchase === 'number' ? brand.purchase : parseInt(brand.purchase, 10) || 0);
            const total = opening + purchase;

            const closingInput = brand.closingBalance === '' || brand.closingBalance === null || brand.closingBalance === undefined
              ? null
              : (typeof brand.closingBalance === 'number' ? brand.closingBalance : parseInt(brand.closingBalance, 10) || 0);
            let sales = brand.sales === '' || brand.sales === null || brand.sales === undefined
              ? 0
              : (typeof brand.sales === 'number' ? brand.sales : parseInt(brand.sales, 10) || 0);

            let closing = closingInput;
            if (closingInput !== null && !Number.isNaN(closingInput)) {
              closing = Math.max(0, closingInput);
              sales = Math.max(0, total - closing);
            } else {
              sales = Math.max(0, sales);
              closing = Math.max(0, total - sales);
            }

            const invoicePrice = brand.invoicePrice === '' || brand.invoicePrice === null || brand.invoicePrice === undefined
              ? 0
              : (typeof brand.invoicePrice === 'number'
                ? brand.invoicePrice
                : parseFloat(brand.invoicePrice) || 0);
            const mrpPrice = brand.mrpPrice === '' || brand.mrpPrice === null || brand.mrpPrice === undefined
              ? 0
              : (typeof brand.mrpPrice === 'number'
                ? brand.mrpPrice
                : parseFloat(brand.mrpPrice) || 0);
            const sellingPrice = brand.sellingPrice === '' || brand.sellingPrice === null || brand.sellingPrice === undefined
              ? 0
              : (typeof brand.sellingPrice === 'number'
                ? brand.sellingPrice
                : parseFloat(brand.sellingPrice) || 0);
            const salesIncome = parseFloat(((sales || 0) * sellingPrice).toFixed(2));

            const rawPackageType = brand.package_type ?? brand.packageType ?? '';
            const packageTypeVal = rawPackageType || (() => {
              const name = brand.name || '';
              const upperName = name.toUpperCase();
              if (upperName.endsWith(' - BTL')) return 'BTL';
              if (upperName.endsWith(' - TPACK')) return 'TPACK';
              if (upperName.endsWith(' - CAN')) return 'CAN';
              return '';
            })();
            return {
              brandId: brand.brandId || brand.id || null,
              categoryId: brand.categoryId || category?.id || null,
              name: brand.name.trim(),
              size: brand.size || '750ml',
              package_type: packageTypeVal,
              packageType: packageTypeVal,
              openingBalance: opening,
              purchase,
              total,
              sales,
              closingBalance: closing,
              invoicePrice: parseFloat(invoicePrice.toFixed(2)),
              mrpPrice: parseFloat(mrpPrice.toFixed(2)),
              sellingPrice: parseFloat(sellingPrice.toFixed(2)),
              salesIncome
            };
          });
        }
      });

      if (Object.keys(stockData).length === 0) {
        toast.error('Please add at least one brand with a name before saving.');
        return;
      }

      const payload = {
        check_date: checkDate,
        stock_data: stockData,
        status: 'Completed'
      };

      const endpoint = currentRecordId
        ? `/stock/check-records/${currentRecordId}`
        : '/stock/check-records';

      if (currentRecordId) {
        await api.put(endpoint, payload);
      } else {
        await api.post(endpoint, payload);
      }

      setDraftStatus('submitted');
      lastSavedStateStrRef.current = null;
      toast.success(currentRecordId ? 'Stock check updated successfully!' : 'Stock check saved successfully!');
      showRecordsList();
    } catch (error) {
      console.error('Error saving stock check:', error);
      
      // Handle different error response formats
      let errorMessage = 'Failed to save stock check';
      
      if (error.response) {
        const data = error.response.data;
        
        // Handle validation errors array
        if (data.errors && Array.isArray(data.errors)) {
          errorMessage = data.errors.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        }
        // Handle single error message
        else if (data.error) {
          errorMessage = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
        }
        // Handle error message directly
        else if (data.message) {
          errorMessage = typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
        }
        // Handle if data itself is a string
        else if (typeof data === 'string') {
          errorMessage = data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleBrandsAdded = async (selectedBrands) => {
    if (!activeCategory || selectedBrands.length === 0) return;

    const category = categories.find(c => c.id === activeCategory);
    if (!category) return;

    const currentBrands = currentStockState[category.name] || [];
    const updatedBrandEntries = [
      ...currentBrands,
      ...selectedBrands.map(brand => {
        const openingBalance = brand.ob || 0;
        const rawPackageType = brand.package_type ?? brand.packageType ?? '';
        const packageTypeVal = rawPackageType || (() => {
          const name = brand.name || '';
          const upperName = name.toUpperCase();
          if (upperName.endsWith(' - BTL')) return 'BTL';
          if (upperName.endsWith(' - TPACK')) return 'TPACK';
          if (upperName.endsWith(' - CAN')) return 'CAN';
          return '';
        })();
        return {
          id: brand.id,
          brandId: brand.id,
          categoryId: category.id,
          name: brand.name,
          size: brand.size || '750ml',
          package_type: packageTypeVal,
          packageType: packageTypeVal,
          openingBalance: openingBalance || '',
          purchase: '',
          total: openingBalance,
          sales: '',
          closingBalance: openingBalance,
          salesIncome: 0,
          invoicePrice: brand.invoice_price || '',
          mrpPrice: brand.mrp_price || '',
          sellingPrice: brand.selling_price || ''
        };
      })
    ];

    const brandIds = updatedBrandEntries
      .map(brand => brand.brandId || brand.id)
      .filter(Boolean);

    try {
      await persistCategoryPreferences(category.id, brandIds);
      setCurrentStockState(prev => ({
        ...prev,
        [category.name]: updatedBrandEntries
      }));
      setMissingCategoryIds(prev => {
        const updated = prev.filter(id => id !== category.id);
        setNeedsSetup(updated.length > 0);
        return updated;
      });
    } catch (error) {
      console.error('Error updating brand preferences:', error);
      toast.error('Failed to save brand preferences. Please try again.');
    }
  };

  const handleBrandsImported = () => {
    loadStockTemplate(checkDate);
  };

  const removeBrand = async (categoryName, index) => {
    const category = categories.find(c => c.name === categoryName);
    if (!category) return;

    const categoryBrands = [...(currentStockState[categoryName] || [])];
    categoryBrands.splice(index, 1);

    const brandIds = categoryBrands
      .map(brand => brand.brandId || brand.id)
      .filter(Boolean);

    try {
      await persistCategoryPreferences(category.id, brandIds);
      setCurrentStockState(prev => ({
        ...prev,
        [categoryName]: categoryBrands
      }));
      setMissingCategoryIds(prev => {
        let updated = prev;
        if (categoryBrands.length === 0) {
          updated = prev.includes(category.id) ? prev : [...prev, category.id];
        } else {
          updated = prev.filter(id => id !== category.id);
        }
        setNeedsSetup(updated.length > 0);
        return updated;
      });
    } catch (error) {
      console.error('Error removing brand preference:', error);
      toast.error('Failed to update brand preferences. Please try again.');
    }
  };

  const updateBrandField = (categoryName, index, field, value) => {
    setCurrentStockState(prev => {
      const categoryBrands = [...(prev[categoryName] || [])];
      const brand = { ...(categoryBrands[index] || {}) };

      // Handle empty values - store as empty string for display, but use 0 for calculations
      if (value === '' || value === null || value === undefined) {
        if (['openingBalance', 'purchase', 'sales', 'closingBalance'].includes(field)) {
          brand[field] = '';
        } else if (['invoicePrice', 'mrpPrice', 'sellingPrice'].includes(field)) {
          brand[field] = '';
        } else {
          brand[field] = value;
        }
      } else if (['openingBalance', 'purchase', 'sales', 'closingBalance'].includes(field)) {
        const parsed = parseInt(value, 10);
        // Ensure non-negative integers
        if (Number.isNaN(parsed) || parsed < 0) {
          brand[field] = '';
        } else {
          brand[field] = parsed;
        }
      } else if (['invoicePrice', 'mrpPrice', 'sellingPrice'].includes(field)) {
        const parsed = typeof value === 'number' ? value : parseFloat(value);
        // Ensure non-negative numbers
        if (Number.isNaN(parsed) || parsed < 0) {
          brand[field] = '';
        } else {
          brand[field] = parseFloat(parsed.toFixed(2));
        }
      } else {
        brand[field] = value;
      }

      // Calculate totals using 0 for empty values
      const opening = brand.openingBalance === '' || brand.openingBalance === null || brand.openingBalance === undefined 
        ? 0 
        : (typeof brand.openingBalance === 'number' ? brand.openingBalance : parseInt(brand.openingBalance, 10) || 0);
      const purchase = brand.purchase === '' || brand.purchase === null || brand.purchase === undefined 
        ? 0 
        : (typeof brand.purchase === 'number' ? brand.purchase : parseInt(brand.purchase, 10) || 0);
      const salesRaw = brand.sales === '' || brand.sales === null || brand.sales === undefined 
        ? 0 
        : (typeof brand.sales === 'number' ? brand.sales : parseInt(brand.sales, 10) || 0);
      const closingRaw = brand.closingBalance === '' || brand.closingBalance === null || brand.closingBalance === undefined
        ? null
        : (typeof brand.closingBalance === 'number' ? brand.closingBalance : parseInt(brand.closingBalance, 10) || 0);
      const sellingRaw = brand.sellingPrice === '' || brand.sellingPrice === null || brand.sellingPrice === undefined
        ? 0
        : (typeof brand.sellingPrice === 'number' ? brand.sellingPrice : parseFloat(brand.sellingPrice) || 0);
      
      brand.total = opening + purchase;

      if (field === 'closingBalance') {
        const closing = Math.max(0, closingRaw ?? 0);
        brand.closingBalance = closing;
        brand.sales = Math.max(0, brand.total - closing);
        brand.isClosingModified = true;
      } else {
        const sales = Math.max(0, salesRaw);
        brand.sales = sales;
        brand.closingBalance = Math.max(0, brand.total - sales);
      }

      brand.salesIncome = parseFloat(((brand.sales || 0) * sellingRaw).toFixed(2));

      categoryBrands[index] = brand;

      return {
        ...prev,
        [categoryName]: categoryBrands
      };
    });
  };

  const getCategorySalesIncome = (categoryName) => {
    const brands = currentStockState[categoryName] || [];
    return brands.reduce((sum, brand) => sum + (brand.salesIncome || 0), 0);
  };

  // Excel-like input handlers
  const handleInputFocus = (e) => {
    if (!isViewMode) {
      e.target.select(); // Select all text on focus for easy replacement
      e.target.style.borderColor = '#4361ee';
      e.target.style.boxShadow = '0 0 0 2px rgba(67, 97, 238, 0.1)';
    }
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = '#d1d5db';
    e.target.style.boxShadow = 'none';
  };

  const handleInputKeyDown = (e, categoryName, idx, field, nextField = null) => {
    // Prevent mouse wheel from changing values
    if (e.type === 'wheel') {
      e.preventDefault();
      return;
    }

    // Enter key moves to next field, and wraps to the next row if at the end of the row
    if (e.key === 'Enter') {
      e.preventDefault();
      const currentRow = e.target.closest('tr');
      const inputs = Array.from(currentRow.querySelectorAll('input'));
      const currentIndex = inputs.indexOf(e.target);
      
      if (currentIndex < inputs.length - 1) {
        inputs[currentIndex + 1].focus();
        inputs[currentIndex + 1].select();
      } else {
        // Move to the first input of the next row
        const nextRow = currentRow.nextElementSibling;
        if (nextRow) {
          const nextRowInputs = nextRow.querySelectorAll('input');
          if (nextRowInputs.length > 0) {
            nextRowInputs[0].focus();
            nextRowInputs[0].select();
          }
        }
      }
    }
    // Arrow keys for navigation
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const currentRow = e.target.closest('tr');
      const nextRow = currentRow.nextElementSibling;
      if (nextRow) {
        const inputs = nextRow.querySelectorAll('input');
        const currentIndex = Array.from(currentRow.querySelectorAll('input')).indexOf(e.target);
        if (inputs[currentIndex]) {
          inputs[currentIndex].focus();
          inputs[currentIndex].select();
        }
      }
    }
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const currentRow = e.target.closest('tr');
      const prevRow = currentRow.previousElementSibling;
      if (prevRow) {
        const inputs = prevRow.querySelectorAll('input');
        const currentIndex = Array.from(currentRow.querySelectorAll('input')).indexOf(e.target);
        if (inputs[currentIndex]) {
          inputs[currentIndex].focus();
          inputs[currentIndex].select();
        }
      }
    }
  };

  const handleWheel = (e) => {
    // Prevent mouse wheel from changing number input values
    if (e.target.type === 'number' && document.activeElement === e.target) {
      e.preventDefault();
      e.target.blur();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Handle both date strings and Date objects
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return dateString; // Return original if invalid date
    
    // Format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getRecordCategoryTotals = (record) => {
    if (!record || !record.stock_data) {
      // Return zeros for all categories if no stock data
      const totals = {};
      categoryOrder.forEach(cat => {
        totals[cat] = 0;
      });
      return totals;
    }

    let stockData;
    try {
      stockData = typeof record.stock_data === 'string' 
        ? JSON.parse(record.stock_data) 
        : record.stock_data;
    } catch (error) {
      console.error('Error parsing stock_data:', error);
      // Return zeros if parsing fails
      const totals = {};
      categoryOrder.forEach(cat => {
        totals[cat] = 0;
      });
      return totals;
    }

    // Ensure stockData is an object
    if (!stockData || typeof stockData !== 'object') {
      const totals = {};
      categoryOrder.forEach(cat => {
        totals[cat] = 0;
      });
      return totals;
    }
    
    // Normalize stock data to ensure consistent field names
    const normalizedStockData = normalizeStockData(stockData);
    
    const totals = {};
    categoryOrder.forEach(cat => {
      const brands = normalizedStockData[cat] || [];
      // Sum the closing balance of all brands in this category
      totals[cat] = brands.reduce((sum, brand) => {
        if (!brand || typeof brand !== 'object') return sum;
        const value = brand.closingBalance ?? brand.closing_balance ?? 0;
        const num = typeof value === 'number' ? value : parseInt(value, 10);
        return sum + (Number.isNaN(num) ? 0 : num);
      }, 0);
    });
    
    return totals;
  };

  // Pagination calculations
  const totalPages = Math.ceil(records.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRecords = records.slice(startIndex, endIndex);

  const printRecord = async (id) => {
    try {
      const response = await api.get(`/stock/check-records/${id}`);
      
      const record = response.data.record;
      if (!record) {
        toast.error('Record not found');
        return;
      }

      // Parse stock_data JSONB
      let stockData = {};
      if (record.stock_data) {
        try {
          stockData = typeof record.stock_data === 'string' 
            ? JSON.parse(record.stock_data) 
            : record.stock_data;
        } catch (error) {
          console.error('Error parsing stock_data:', error);
          toast.error('Failed to parse stock data');
          return;
        }
      }

      // Ensure stockData is an object
      if (!stockData || typeof stockData !== 'object') {
        stockData = {};
      }

      // Normalize stock data to ensure consistent field names
      const normalizedStockData = normalizeStockData(stockData);

      // Build HTML content
      let content = `
        <html>
          <head>
            <title>Stock Check - ${record.check_date}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; font-size: 10px; color: #333; }
              h1 { color: #0052D4; font-size: 24px; text-align: center; }
              h2 { color: #0052D4; font-size: 16px; border-bottom: 2px solid #0052D4; padding-bottom: 5px; margin-top: 20px; }
              .record-details { margin: 20px 0; font-size: 12px; background: #f4f7f6; border-radius: 8px; padding: 15px; border: 1px solid #e6e9ef; }
              .record-details div { margin: 5px 0; }
              table { width: 100%; border-collapse: collapse; margin-top: 10px; margin-bottom: 15px; }
              th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
              th { background-color: #f2f2f2; font-weight: bold; }
              .total-row td { font-weight: bold; background-color: #e9ecef; border-top: 2px solid #ccc; }
            </style>
          </head>
          <body>
            <h1>Stock Check Record</h1>
            <div class="record-details">
              <div><strong>Date:</strong> ${record.check_date}</div>
              <div><strong>Total Categories:</strong> ${record.total_categories}</div>
              <div><strong>Total Bottles:</strong> ${record.total_bottles}</div>
              <div><strong>Status:</strong> ${record.status}</div>
            </div>
            
            <h2>Summary by Category</h2>
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Total Bottles</th>
                </tr>
              </thead>
              <tbody>
      `;

      // Calculate category totals for summary
      const categoryTotals = {};
      let grandTotal = 0;
      for (const category of categoryOrder) {
        if (normalizedStockData[category] && Array.isArray(normalizedStockData[category])) {
          const brands = normalizedStockData[category].filter(brand => brand && brand.name && brand.name.trim());
          const total = brands.reduce((sum, brand) => {
            const closing = brand.closingBalance !== undefined && brand.closingBalance !== null
              ? brand.closingBalance
              : brand.count || 0;
            return sum + closing;
          }, 0);
          categoryTotals[category] = total;
          grandTotal += total;
        } else {
          categoryTotals[category] = 0;
        }
      }

      // Add summary rows
      categoryOrder.forEach(cat => {
        content += `
          <tr>
            <td>${cat}</td>
            <td>${categoryTotals[cat]}</td>
          </tr>
        `;
      });
      
      content += `
                <tr class="total-row">
                  <td><strong>Total</strong></td>
                  <td><strong>${grandTotal}</strong></td>
                </tr>
              </tbody>
            </table>
      `;

      // Loop through categories for detailed view
      for (const category of categoryOrder) {
        if (normalizedStockData[category] && Array.isArray(normalizedStockData[category]) && normalizedStockData[category].length > 0) {
          const brands = normalizedStockData[category].filter(brand => brand && brand.name && brand.name.trim());
          
          if (brands.length > 0) {
            const categoryBottles = brands.reduce((sum, brand) => sum + (brand.closingBalance || 0), 0);
            content += `<h2>${category} (Closing Balance: ${categoryBottles})</h2>`;
            content += `
              <table>
                <thead>
                  <tr>
                    <th>Brand Name</th>
                    <th>Size</th>
                    <th>Opening</th>
                    <th>Purchase</th>
                    <th>Total</th>
                  <th>Sales</th>
                  <th>Closing</th>
                  <th>Selling Price</th>
                  <th>Sales Income</th>
                  </tr>
                </thead>
                <tbody>
            `;
            
            let catTotalOpening = 0;
            let catTotalPurchase = 0;
            let catTotalAll = 0;
            let catTotalSales = 0;
            let catTotalClosing = 0;
            let catTotalSalesIncome = 0;

            brands.forEach(brand => {
              const opening = brand.openingBalance ?? 0;
              const purchase = brand.purchase ?? 0;
              const totalValue = brand.total ?? (opening + purchase);
              const sales = brand.sales ?? 0;
              const closing = brand.closingBalance ?? Math.max(0, totalValue - sales);
              const sellingPrice = brand.sellingPrice !== undefined && brand.sellingPrice !== null ? brand.sellingPrice : 0;
              const salesIncome = brand.salesIncome !== undefined && brand.salesIncome !== null
                ? brand.salesIncome
                : parseFloat((sales * sellingPrice).toFixed(2));

              content += `
                <tr>
                  <td>${(brand.name || '').replace(/\s*-\s*(BTL|TPACK|CAN)$/i, '')}</td>
                  <td>${brand.size || '750ml'}</td>
                  <td>${opening}</td>
                  <td>${purchase}</td>
                  <td>${totalValue}</td>
                  <td>${sales}</td>
                  <td>${closing}</td>
                  <td>${sellingPrice.toFixed(2)}</td>
                  <td>${salesIncome.toFixed(2)}</td>
                </tr>
              `;

              catTotalOpening += opening;
              catTotalPurchase += purchase;
              catTotalAll += totalValue;
              catTotalSales += sales;
              catTotalClosing += closing;
              catTotalSalesIncome += salesIncome;
            });
            
            content += `
                <tr class="total-row">
                  <td colspan="2" style="text-align: right;"><strong>Category Total:</strong></td>
                  <td><strong>${catTotalOpening}</strong></td>
                  <td><strong>${catTotalPurchase}</strong></td>
                  <td><strong>${catTotalAll}</strong></td>
                  <td><strong>${catTotalSales}</strong></td>
                  <td><strong>${catTotalClosing}</strong></td>
                  <td><strong>-</strong></td>
                  <td><strong>${catTotalSalesIncome.toFixed(2)}</strong></td>
                </tr>
            `;
            
            content += `</tbody></table>`;
          }
        }
      }
      
      content += `</body></html>`;
      
      // Generate PDF
      const filename = `StockCheck_${record.check_date.replace(/\//g, '-')}.pdf`;
      const opt = {
        margin: 0.5,
        filename: filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, letterRendering: true },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'landscape' }
      };
      
      if (html2pdf) {
        html2pdf().set(opt).from(content).save();
      } else {
        toast.error('PDF generation library not loaded');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <div className="max-w-[99%] mx-auto px-2 sm:px-4">
        <LoadingState label="Loading stock check..." />
      </div>
    );
  }

  // Records List View
  if (view === 'list') {
    return (
      <div className="max-w-[99%] mx-auto px-2 sm:px-4">
        <PageHeader
          icon="fa-boxes"
          title="Stock Check Records"
          subtitle="Review and manage saved stock check entries"
          actions={hasPermission('stock_check', 'create') && (
            <Button icon="fa-plus" onClick={createNewStockCheck}>Create Stock Check</Button>
          )}
        />

        {/* Records Table */}
        <Card padding="none" className="overflow-hidden">
          <h4 className="text-base font-semibold text-gray-800 dark:text-slate-100 p-4 sm:p-6 pb-0">
            Stock Check History
          </h4>
          {records.length === 0 ? (
            <EmptyState
              icon="fa-boxes"
              title="No stock check records found"
              description='Click "Create Stock Check" to add your first record.'
            />
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden lg:block p-4 sm:p-6">
                <Table>
                  <THead>
                    <tr>
                      <Th>S.No</Th>
                      <Th>Date</Th>
                      {categoryOrder.map(cat => (
                        <Th key={cat}>{cat === 'RTD' ? 'RTD (breezer)' : cat}</Th>
                      ))}
                      <Th>Total</Th>
                      <Th className="min-w-[190px]">Actions</Th>
                    </tr>
                  </THead>
                  <tbody>
                    {paginatedRecords.map((record, index) => {
                      const categoryTotals = getRecordCategoryTotals(record);
                      const recordTotal = Object.values(categoryTotals).reduce((sum, val) => sum + val, 0);
                      const serialNumber = startIndex + index + 1;
                      return (
                        <Tr key={record.id} id={`record-${record.id}`}>
                          <Td>{serialNumber}</Td>
                          <Td>{formatDate(record.check_date)}</Td>
                          {categoryOrder.map(cat => (
                            <Td key={cat}>{categoryTotals[cat]}</Td>
                          ))}
                          <Td className="font-bold text-gray-800 dark:text-slate-100">{recordTotal}</Td>
                          <Td>
                            <div className="flex flex-wrap gap-1.5">
                              {hasPermission('stock_check', 'view') && (
                                <Button size="sm" variant="info" icon="fa-eye" onClick={() => viewRecord(record.id)}>View</Button>
                              )}
                              {hasPermission('stock_check', 'edit') && (
                                <Button size="sm" variant="success" icon="fa-edit" onClick={() => editRecord(record.id)}>Edit</Button>
                              )}
                              {enableStockCheckBrandDeletion && hasPermission('stock_check', 'delete') && (
                                <Button size="sm" variant="danger" icon="fa-trash" onClick={() => deleteRecord(record.id)}>Delete</Button>
                              )}
                              {hasPermission('stock_check', 'view') && (
                                <Button size="sm" variant="purple" icon="fa-file-pdf" onClick={() => printRecord(record.id)}>PDF</Button>
                              )}
                            </div>
                          </Td>
                        </Tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Mobile cards */}
              <div className="lg:hidden divide-y divide-gray-100">
                {paginatedRecords.map((record, index) => {
                  const categoryTotals = getRecordCategoryTotals(record);
                  const recordTotal = Object.values(categoryTotals).reduce((sum, val) => sum + val, 0);
                  const serialNumber = startIndex + index + 1;
                  const nonZeroCategories = categoryOrder.filter(cat => categoryTotals[cat] > 0);
                  return (
                    <div key={record.id} id={`record-${record.id}`} className="p-4 space-y-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="text-xs text-gray-400 dark:text-slate-500">#{serialNumber}</span>
                          <h5 className="font-bold text-gray-800 dark:text-slate-100">{formatDate(record.check_date)}</h5>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-gray-400 dark:text-slate-500 block">Total</span>
                          <span className="font-bold text-primary text-lg">{recordTotal}</span>
                        </div>
                      </div>
                      {nonZeroCategories.length > 0 && (
                        <div className="grid grid-cols-3 gap-1.5 text-xs">
                          {nonZeroCategories.map(cat => (
                            <div key={cat} className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-1.5 text-center">
                              <div className="text-gray-400 dark:text-slate-500 truncate">{cat === 'RTD' ? 'RTD' : cat}</div>
                              <div className="font-bold text-gray-700 dark:text-slate-300">{categoryTotals[cat]}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {hasPermission('stock_check', 'view') && (
                          <Button size="sm" variant="info" icon="fa-eye" onClick={() => viewRecord(record.id)} className="flex-1">View</Button>
                        )}
                        {hasPermission('stock_check', 'edit') && (
                          <Button size="sm" variant="success" icon="fa-edit" onClick={() => editRecord(record.id)} className="flex-1">Edit</Button>
                        )}
                        {enableStockCheckBrandDeletion && hasPermission('stock_check', 'delete') && (
                          <Button size="sm" variant="danger" icon="fa-trash" onClick={() => deleteRecord(record.id)} className="flex-1">Delete</Button>
                        )}
                        {hasPermission('stock_check', 'view') && (
                          <Button size="sm" variant="purple" icon="fa-file-pdf" onClick={() => printRecord(record.id)} className="flex-1">PDF</Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="mt-1 pt-4 pb-4 sm:pb-6 border-t border-gray-100 dark:border-slate-700/60 flex flex-col items-center gap-2">
                  <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                  <span className="text-xs font-semibold text-gray-500 dark:text-slate-400">Page {currentPage} of {totalPages}</span>
                </div>
              )}
            </>
          )}
        </Card>

        {/* Draft Blocker Modal (List View) */}
        <DraftBlockerModal
          isOpen={showDraftBlocker}
          draftToBlock={draftToBlock}
          onDelete={deleteBlockedDraft}
          onContinue={continueBlockedDraft}
        />
      </div>
    );
  }

  // Stock Check Form View
  const activeCategoryName = categories.find(c => c.id === activeCategory)?.name || '';
  const currentBrands = currentStockState[activeCategoryName] || [];
  const filteredBrands = currentBrands.filter(brand => {
    const matchesSearch = brand.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMl = !mlFilter || brand.size?.toLowerCase().includes(mlFilter.toLowerCase());
    const brandPackage = brand.package_type || brand.packageType || '';
    const matchesPackage = !packageFilter || brandPackage.toLowerCase() === packageFilter.toLowerCase();
    return matchesSearch && matchesMl && matchesPackage;
  });

  return (
    <div className="max-w-[99%] mx-auto px-2 sm:px-4 animate-slide-in">
      <style>{`
        /* Hide number input spinners */
        input[type="number"]::-webkit-outer-spin-button,
        input[type="number"]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"] {
          -moz-appearance: textfield;
        }
        /* Excel-like input styling */
        .excel-input {
          border: 1px solid #e2e8f0 !important;
          border-radius: 6px !important;
          padding: 4px 8px !important;
          font-size: 12px !important;
          background: #ffffff !important;
          color: #0f172a !important;
          transition: all 0.2s ease !important;
          height: 28px !important;
        }
        .excel-input:focus {
          border-color: #4361ee !important;
          box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.15) !important;
          outline: none !important;
        }
        .excel-input:hover:not(:focus):not(:disabled) {
          border-color: #cbd5e1 !important;
        }
        .dark .excel-input {
          border-color: #475569 !important;
          background: #1e293b !important;
          color: #f1f5f9 !important;
        }
        .dark .excel-input:focus {
          border-color: #4cc9f0 !important;
          box-shadow: 0 0 0 3px rgba(76, 201, 240, 0.15) !important;
        }
        .dark .excel-input:hover:not(:focus):not(:disabled) {
          border-color: #64748b !important;
        }
        /* Custom scrollbar */
        .custom-scrollbar::-webkit-scrollbar {
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        /* High density table styles */
        table th {
          padding: 8px 10px !important;
          font-size: 11px !important;
        }
        table td {
          padding: 4px 8px !important;
          font-size: 13px !important;
        }
        /* Mobile card view compaction */
        .md\:hidden .space-y-3 > div {
          padding: 10px !important;
          margin-bottom: 8px !important;
          gap: 8px !important;
        }
        .md\:hidden .grid-cols-2 {
          gap: 6px !important;
        }
        .md\:hidden .grid-cols-2 input, 
        .md\:hidden .grid-cols-2 div {
          padding: 4px 8px !important;
          min-height: 28px !important;
          height: 28px !important;
          font-size: 12px !important;
        }
        .md\:hidden button.w-full {
          height: 28px !important;
          font-size: 12px !important;
          padding: 0 !important;
        }
      `}</style>

      {/* Sticky Header, Tabs & Filters Container */}
      <div className="sticky top-0 z-30 bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur-md pt-1.5 pb-2 mb-2 sm:pt-2 sm:pb-3 sm:mb-3 space-y-2 sm:space-y-3 -mx-4 px-4 sm:-mx-6 sm:px-6">
        {/* Header */}
        <div className="bg-white dark:bg-slate-800 rounded-xl p-1.5 sm:p-2.5 shadow-md border border-slate-200/50 flex flex-wrap md:flex-nowrap items-center justify-between gap-1.5 sm:gap-2.5 transition-all">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-tr from-primary to-accent flex items-center justify-center text-white shadow-md shadow-primary/20 shrink-0">
              <i className="fas fa-boxes text-[10px] sm:text-xs"></i>
            </div>
            <div>
              <h1 className="text-xs sm:text-base font-bold text-slate-800 dark:text-slate-100 flex items-center gap-1 sm:gap-1.5">
                {isViewMode ? 'View Stock' : 'Stock Check'}
                <span className={`text-[7px] sm:text-[8px] px-1 sm:px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${isViewMode ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 border border-indigo-100' : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-100'}`}>
                  {isViewMode ? 'Read Only' : 'Active'}
                </span>
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2.5 ml-auto md:ml-0">
            <div className="flex items-center gap-0.5 sm:gap-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 rounded-lg px-1.5 py-0.5 sm:px-2 sm:py-1 shrink-0">
              <label htmlFor="stock-check-date" className="text-[9px] sm:text-[10px] font-semibold text-slate-500 flex items-center gap-0.5 shrink-0">
                <i className="far fa-calendar text-[8px] sm:text-[9px]"></i> Date:
              </label>
              <input
                id="stock-check-date"
                type="date"
                value={checkDate}
                onChange={(e) => handleDateChange(e.target.value)}
                disabled={isViewMode}
                className="bg-transparent border-none outline-none text-[10px] sm:text-xs font-semibold text-slate-700 dark:text-slate-200 w-[85px] sm:w-[100px] py-0 px-0 focus:ring-0 focus:border-none"
              />
            </div>

            <div className="flex gap-1 items-center shrink-0 ml-auto sm:ml-0">
              {!isViewMode && !currentRecordId && !isTodayLocked && (
                <div className="hidden sm:flex items-center gap-2 mr-2">
                   {draftStatus === 'saving' && (
                     <span className="text-[10px] text-blue-500 flex items-center gap-1 font-medium bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                       <i className="fas fa-circle-notch fa-spin"></i> 🔵 Auto Saving...
                     </span>
                   )}
                   {draftStatus === 'saved' && (
                     <span className="text-[10px] text-amber-600 flex items-center gap-1 font-medium bg-amber-50 px-2 py-0.5 rounded border border-amber-100">
                       <i className="fas fa-save"></i> 🟡 Draft Saved
                     </span>
                   )}
                   {draftStatus === 'offline' && (
                     <span className="text-[10px] text-red-500 flex items-center gap-1 font-medium bg-red-50 px-2 py-0.5 rounded border border-red-100">
                       <i className="fas fa-wifi-slash"></i> 🔴 Offline (Pending)
                     </span>
                   )}
                   {draftStatus === 'submitted' && (
                     <span className="text-[10px] text-emerald-600 flex items-center gap-1 font-medium bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">
                       <i className="fas fa-check-double"></i> 🟢 Final Submitted
                     </span>
                   )}
                   {lastSaveTime && draftStatus === 'saved' && (
                     <span className="text-[10px] text-slate-400 font-medium ml-1">
                       Last Saved: {lastSaveTime}
                     </span>
                   )}
                </div>
              )}
              
              {!isViewMode && !currentRecordId && !isTodayLocked && (currentRecordId ? hasPermission('stock_check', 'edit') : hasPermission('stock_check', 'create')) && (
                <button 
                  onClick={() => autoSaveDraft(true)}
                  disabled={draftStatus === 'saving'}
                  className="flex items-center justify-center gap-0.5 sm:gap-1 px-2 py-1 sm:px-3 sm:py-1.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-800 rounded-lg text-[10px] sm:text-xs font-bold hover:bg-amber-200 hover:shadow-lg transition-all duration-150 shrink-0 disabled:opacity-50"
                  title="Manually save your draft"
                >
                  <i className="fas fa-save text-[9px] sm:text-xs"></i> Save Draft
                </button>
              )}
              
              {!isViewMode && !isTodayLocked && (currentRecordId ? hasPermission('stock_check', 'edit') : hasPermission('stock_check', 'create')) && (
                <button 
                  onClick={saveStockCheck}
                  className="flex items-center justify-center gap-0.5 sm:gap-1 px-2 py-1 sm:px-3 sm:py-1.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg text-[10px] sm:text-xs font-bold hover:shadow-lg hover:shadow-emerald-500/25 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-150 shrink-0"
                  title="Permanently save and submit"
                >
                  <i className="fas fa-check text-[9px] sm:text-xs"></i> Final Save
                </button>
              )}
              <button 
                onClick={showRecordsList}
                className="flex items-center justify-center gap-0.5 sm:gap-1 px-2 py-1 sm:px-3 sm:py-1.5 bg-white dark:bg-slate-800 border border-slate-200 text-slate-700 dark:text-slate-200 rounded-lg text-[10px] sm:text-xs font-semibold hover:bg-slate-50 hover:border-slate-300 active:bg-slate-100 transition-all duration-150 shadow-sm shrink-0"
              >
                <i className="fas fa-arrow-left text-[9px] sm:text-xs"></i> Back
              </button>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="bg-slate-100/80 backdrop-blur-sm rounded-2xl p-0.5 sm:p-1 border border-slate-200/50 flex gap-1 sm:gap-1.5 overflow-x-auto w-full shadow-inner custom-scrollbar" style={{ WebkitOverflowScrolling: 'touch' }}>
          {categories.map((category) => {
            const isActive = activeCategory === category.id;
            const brandCount = currentStockState[category.name]?.length || 0;
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`whitespace-nowrap px-2 py-1 sm:px-3 sm:py-1.5 min-h-[38px] sm:min-h-0 rounded-lg font-bold text-[10px] sm:text-xs border transition-all duration-200 shrink-0 flex items-center gap-1 sm:gap-1.5 ${
                  isActive
                    ? 'bg-gradient-to-r from-primary to-primary-dark text-white shadow-md shadow-primary/20 border-transparent hover:shadow-lg'
                    : 'bg-white dark:bg-slate-800/80 text-slate-600 border-slate-200/60 hover:bg-white hover:text-slate-900 hover:border-slate-300 hover:shadow-sm'
                }`}
              >
                <span>{category.name === 'RTD' ? 'RTD (breezer)' : category.name}</span>
                <span className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded-md text-[8px] sm:text-[10px] font-bold ${
                  isActive 
                    ? 'bg-white text-primary' 
                    : brandCount > 0 
                      ? 'bg-primary/10 text-primary' 
                      : 'bg-slate-200 text-slate-400'
                }`}>
                  {brandCount}
                </span>
              </button>
            );
          })}
        </div>

        {/* Filters & Actions Panel */}
        <div className="bg-white dark:bg-slate-800 rounded-xl p-1.5 sm:p-2.5 shadow-md border border-slate-200/50">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-1.5 sm:gap-2.5">
            {/* Left: Category Title & Actions (Compact on Mobile) */}
            <div className="flex items-center justify-between lg:justify-start gap-3 w-full lg:w-auto">
              <div className="flex items-center gap-1.5">
                <h2 className="text-xs sm:text-base font-bold text-slate-800 dark:text-slate-100">
                  {activeCategoryName === 'RTD' ? 'RTD (breezer)' : (activeCategoryName || 'Module')}
                </h2>
                <span className="text-[8px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded font-bold font-mono">
                  {filteredBrands.length} {filteredBrands.length === 1 ? 'brand' : 'brands'}
                </span>
              </div>
              
              {/* Actions on Mobile (Compact) */}
              {!isViewMode && (hasPermission('stock_check', 'import') || (currentRecordId ? hasPermission('stock_check', 'edit') : hasPermission('stock_check', 'create'))) && (
                <div className="flex gap-1 sm:gap-1.5 lg:hidden">
                  {hasPermission('stock_check', 'import') && (
                    <button 
                      onClick={() => setShowImportBrandsModal(true)}
                      className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[10px] sm:text-xs font-semibold hover:shadow-lg transition-all duration-150 shrink-0"
                      title="Import Brands"
                    >
                      <i className="fas fa-file-import"></i>
                    </button>
                  )}
                  {(currentRecordId ? hasPermission('stock_check', 'edit') : hasPermission('stock_check', 'create')) && (
                    <button 
                      onClick={() => setShowAddBrandModal(true)}
                      className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 bg-primary hover:bg-primary-dark text-white rounded-lg text-[10px] sm:text-xs font-semibold hover:shadow-lg transition-all duration-150 shrink-0"
                      title="Add Brand"
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Right: Search & Filters & Desktop Actions */}
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-1.5 sm:gap-2 w-full lg:w-auto">
              {/* Search */}
              <div className="relative flex-1 sm:flex-none w-full sm:w-[160px] md:w-[200px]">
                <span className="absolute inset-y-0 left-0 pl-2 sm:pl-2.5 flex items-center pointer-events-none">
                  <i className="fas fa-search text-slate-400 text-[10px] sm:text-xs"></i>
                </span>
                <input
                  id="stock-brand-search"
                  type="text"
                  placeholder="Search brand..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-7 sm:pl-8 pr-2 py-1 sm:py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 outline-none text-[10px] sm:text-xs focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-sm font-medium dark:bg-slate-800 dark:text-slate-100"
                />
              </div>

              {/* Size filter */}
              <div className="relative flex-1 sm:flex-none w-[48%] sm:w-auto">
                <select
                  id="stock-ml-filter"
                  value={mlFilter}
                  onChange={(e) => setMlFilter(e.target.value)}
                  className="w-full sm:w-auto pl-1.5 pr-6 sm:pl-2 sm:pr-7 py-1 sm:py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 outline-none text-[10px] sm:text-xs bg-white dark:bg-slate-800 cursor-pointer focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-sm appearance-none font-medium dark:text-slate-100"
                >
                  <option value="">All Sizes</option>
                  <option value="180ml">180ml</option>
                  <option value="375ml">375ml</option>
                  <option value="500ml">500ml</option>
                  <option value="750ml">750ml</option>
                  <option value="1000ml">1000ml</option>
                  <option value="1ltr">1LTR</option>
                </select>
                <span className="absolute inset-y-0 right-0 pr-2 sm:pr-2.5 flex items-center pointer-events-none">
                  <i className="fas fa-chevron-down text-slate-400 text-[8px] sm:text-[9px]"></i>
                </span>
              </div>

              {/* Pack filter */}
              <div className="relative flex-1 sm:flex-none w-[48%] sm:w-auto">
                <select
                  id="stock-package-filter"
                  value={packageFilter}
                  onChange={(e) => setPackageFilter(e.target.value)}
                  className="w-full sm:w-auto pl-1.5 pr-6 sm:pl-2 sm:pr-7 py-1 sm:py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 outline-none text-[10px] sm:text-xs bg-white dark:bg-slate-800 cursor-pointer focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-sm appearance-none font-medium dark:text-slate-100"
                >
                  <option value="">All Packaging</option>
                  <option value="BTL">BTL (Bottle)</option>
                  <option value="TPACK">TPACK (Tetra Pack)</option>
                  <option value="CAN">CAN</option>
                </select>
                <span className="absolute inset-y-0 right-0 pr-2 sm:pr-2.5 flex items-center pointer-events-none">
                  <i className="fas fa-chevron-down text-slate-400 text-[8px] sm:text-[9px]"></i>
                </span>
              </div>

              {/* Desktop Actions */}
              {!isViewMode && (hasPermission('stock_check', 'import') || (currentRecordId ? hasPermission('stock_check', 'edit') : hasPermission('stock_check', 'create'))) && (
                <div className="hidden lg:flex gap-2 shrink-0">
                  {hasPermission('stock_check', 'import') && (
                    <button 
                      onClick={() => setShowImportBrandsModal(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold hover:shadow-lg transition-all duration-150 shrink-0"
                    >
                      <i className="fas fa-file-import text-xs"></i> Import Brands
                    </button>
                  )}
                  {(currentRecordId ? hasPermission('stock_check', 'edit') : hasPermission('stock_check', 'create')) && (
                    <button 
                      onClick={() => setShowAddBrandModal(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-primary-dark text-white rounded-lg text-xs font-semibold hover:shadow-lg transition-all duration-150 shrink-0"
                    >
                      <i className="fas fa-plus text-xs"></i> Add Brand
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <Card padding="none" className="p-3 sm:p-4 shadow-lg border border-slate-200/60 rounded-2xl overflow-hidden bg-white dark:bg-slate-800">

        {/* Brand Table */}
        <div
          className="hidden md:block overflow-auto pr-1 border border-slate-100 rounded-xl max-h-dvh-64"
          style={{ WebkitOverflowScrolling: 'touch' }}
          onWheel={handleWheel}
        >
          {templateLoading ? (
            <div className="text-center py-12 text-slate-500 font-medium flex flex-col items-center gap-3">
              <i className="fas fa-spinner fa-spin text-2xl text-primary"></i>
              <span>Loading category templates...</span>
            </div>
          ) : (
          <table className="w-full border-collapse">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="sticky top-0 bg-slate-50 z-10 px-4 py-4 text-center border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider w-[60px]">S.No</th>
                <th className="sticky top-0 left-0 bg-slate-50 dark:bg-slate-900 z-20 px-4 py-4 text-left border-b border-r border-slate-200 dark:border-slate-700 text-xs font-bold text-slate-500 uppercase tracking-wider min-w-[320px] w-[35%] break-words">Brand Name</th>
                <th className="sticky top-0 bg-slate-50 z-10 px-4 py-4 text-center border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider w-[100px]">ML/LTR</th>
                <th className="sticky top-0 bg-slate-50 z-10 px-4 py-4 text-right border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider w-[130px]">Opening</th>
                <th className="sticky top-0 bg-slate-50 z-10 px-4 py-4 text-right border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider w-[130px]">Purchase</th>
                <th className="sticky top-0 bg-slate-50 z-10 px-4 py-4 text-right border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider w-[100px]">Total</th>
                <th className="sticky top-0 bg-slate-50 z-10 px-4 py-4 text-right border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider w-[100px]">Sales</th>
                <th className="sticky top-0 bg-slate-50 z-10 px-4 py-4 text-right border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider w-[130px]">Closing</th>
                <th className="sticky top-0 bg-slate-50 z-10 px-4 py-4 text-right border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider w-[130px]">Selling Price</th>
                <th className="sticky top-0 bg-slate-50 z-10 px-4 py-4 text-right border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider w-[140px]">Sales Income</th>
                {enableStockCheckBrandDeletion && !isViewMode && (currentRecordId ? hasPermission('stock_check', 'edit') : hasPermission('stock_check', 'create')) && (
                  <th className="sticky top-0 bg-slate-50 z-10 px-4 py-4 text-center border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider w-[80px]">Actions</th>
                )}
              </tr>
            </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredBrands.length === 0 ? (
                  <tr>
                    <td
                      colSpan={(10 + ((enableStockCheckBrandDeletion && !isViewMode && (currentRecordId ? hasPermission('stock_check', 'edit') : hasPermission('stock_check', 'create'))) ? 1 : 0))}
                      className="text-center py-12 text-slate-400 text-sm font-medium"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <i className="fas fa-clipboard-list text-2xl text-slate-300"></i>
                        <span>{searchTerm ? 'No brands match your search.' : 'No brands found. Use "+ Add Brand" to setup products.'}</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredBrands.map((brand, idx) => {
                    // Find the actual index in the original array - use brandId/id if available, otherwise match by name and size
                    const originalIdx = currentBrands.findIndex(b => {
                      if ((b.brandId || b.id) && (brand.brandId || brand.id)) {
                        return (b.brandId || b.id) === (brand.brandId || brand.id);
                      }
                      return b.name === brand.name && (b.size || '') === (brand.size || '');
                    });
                    const isHighlighted = searchTerm && brand.name.toLowerCase().includes(searchTerm.toLowerCase());
                    const isBrandFromModal = !!(brand.brandId || brand.id); // Brand selected from modal
                    const isReadOnly = isViewMode || isBrandFromModal;
                    const actualIdx = originalIdx >= 0 ? originalIdx : idx; // Use original index if found, otherwise use filtered index

                    return (
                    <tr key={idx} className={`hover:bg-slate-50/70 transition-colors ${isHighlighted ? 'bg-amber-50/80 dark:bg-amber-500/10 hover:bg-amber-100/80' : ''}`}>
                      {/* S.No */}
                      <td className="px-4 py-3.5 text-center border-b border-slate-100 text-sm">
                        <div className="inline-flex justify-center items-center w-6 h-6 rounded-lg bg-slate-100 text-[11px] font-bold text-slate-500 font-mono">
                          {idx + 1}
                        </div>
                      </td>

                      {/* Brand Name */}
                      <td className="sticky left-0 bg-white dark:bg-slate-800 z-10 px-4 py-3.5 text-left border-b border-r border-slate-100 dark:border-slate-700 text-sm max-w-[450px] break-words whitespace-normal">
                        {isReadOnly ? (
                          <div className="font-semibold text-slate-800 text-sm max-w-[380px] break-words leading-tight py-1">
                            {brand.name.replace(/\s*-\s*(BTL|TPACK|CAN)$/i, '')}
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={brand.name || ''}
                            onChange={(e) => updateBrandField(activeCategoryName, actualIdx, 'name', e.target.value)}
                            className="w-full excel-input text-gray-900 dark:text-slate-100 border border-slate-200 rounded-lg py-2 px-3 focus:outline-none focus:border-primary text-sm font-semibold"
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onKeyDown={(e) => handleInputKeyDown(e, activeCategoryName, actualIdx, 'name', 'size')}
                            onWheel={(e) => e.preventDefault()}
                          />
                        )}
                      </td>

                      {/* ML/LTR Size */}
                      <td className="px-4 py-3.5 text-center border-b border-slate-100 text-sm">
                        {isReadOnly ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-bold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 border border-indigo-100 font-mono whitespace-nowrap">
                            {brand.size || '750ml'}{brand.package_type || brand.packageType ? ` - ${brand.package_type || brand.packageType}` : ''}
                          </span>
                        ) : (
                          <input
                            type="text"
                            value={brand.size || ''}
                            onChange={(e) => updateBrandField(activeCategoryName, actualIdx, 'size', e.target.value)}
                            className="w-16 text-center excel-input text-gray-900 dark:text-slate-100 border border-slate-200 rounded-lg py-1.5 px-2 focus:outline-none focus:border-primary text-xs font-bold font-mono"
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onKeyDown={(e) => handleInputKeyDown(e, activeCategoryName, actualIdx, 'size', 'openingBalance')}
                            onWheel={(e) => e.preventDefault()}
                          />
                        )}
                      </td>

                      {/* Opening Balance */}
                      <td className="px-4 py-3.5 text-right border-b border-slate-100 text-sm">
                        {isViewMode ? (
                          <div className="text-right font-semibold text-slate-700 font-mono pr-2.5">
                            {brand.openingBalance || 0}
                          </div>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            value={brand.openingBalance === 0 || brand.openingBalance === null || brand.openingBalance === undefined ? '' : brand.openingBalance}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val.replace(/[^0-9]/g, '').length > 7) return;
                              if (val === '' || val === '-') {
                                updateBrandField(activeCategoryName, actualIdx, 'openingBalance', val === '-' ? '' : val);
                              } else {
                                const parsed = parseInt(val, 10);
                                if (!Number.isNaN(parsed) && parsed >= 0) {
                                  updateBrandField(activeCategoryName, actualIdx, 'openingBalance', parsed);
                                }
                              }
                            }}
                            placeholder="0"
                            className="w-24 text-right excel-input text-gray-900 dark:text-slate-100 border border-slate-200 rounded-lg py-2 px-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all text-sm font-semibold font-mono shadow-sm"
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onKeyDown={(e) => handleInputKeyDown(e, activeCategoryName, actualIdx, 'openingBalance', 'purchase')}
                            onWheel={(e) => e.preventDefault()}
                          />
                        )}
                      </td>

                      {/* Purchase */}
                      <td className="px-4 py-3.5 text-right border-b border-slate-100 text-sm">
                        {isViewMode ? (
                          <div className="text-right font-semibold text-slate-700 font-mono pr-2.5">
                            {brand.purchase || 0}
                          </div>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            value={brand.purchase === 0 || brand.purchase === null || brand.purchase === undefined ? '' : brand.purchase}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val.replace(/[^0-9]/g, '').length > 7) return;
                              if (val === '' || val === '-') {
                                updateBrandField(activeCategoryName, actualIdx, 'purchase', val === '-' ? '' : val);
                              } else {
                                const parsed = parseInt(val, 10);
                                if (!Number.isNaN(parsed) && parsed >= 0) {
                                  updateBrandField(activeCategoryName, actualIdx, 'purchase', parsed);
                                }
                              }
                            }}
                            placeholder="0"
                            className="w-24 text-right excel-input text-gray-900 dark:text-slate-100 border border-slate-200 rounded-lg py-2 px-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all text-sm font-semibold font-mono shadow-sm"
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onKeyDown={(e) => handleInputKeyDown(e, activeCategoryName, actualIdx, 'purchase', 'sales')}
                            onWheel={(e) => e.preventDefault()}
                          />
                        )}
                      </td>

                      {/* Total */}
                      <td className="px-4 py-3.5 text-right border-b border-slate-100 text-sm text-slate-800 font-bold font-mono pr-2.5">
                        {brand.total ?? 0}
                      </td>

                      {/* Sales */}
                      <td className="px-4 py-3.5 text-right border-b border-slate-100 text-sm">
                        <div className="text-right font-semibold text-slate-700 font-mono pr-2.5">
                          {brand.sales ?? 0}
                        </div>
                      </td>

                      {/* Closing Balance */}
                      <td className="px-4 py-3.5 text-right border-b border-slate-100 text-sm">
                        {isViewMode ? (
                          <div className="inline-flex items-center justify-end font-bold text-slate-900 font-mono bg-slate-100/60 px-2.5 py-1 rounded-lg border border-slate-200/50">
                            {brand.closingBalance || 0}
                          </div>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            value={brand.closingBalance === 0 || brand.closingBalance === null || brand.closingBalance === undefined ? '' : brand.closingBalance}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val.replace(/[^0-9]/g, '').length > 7) return;
                              if (val === '' || val === '-') {
                                updateBrandField(activeCategoryName, actualIdx, 'closingBalance', val === '-' ? '' : val);
                              } else {
                                const parsed = parseInt(val, 10);
                                if (!Number.isNaN(parsed) && parsed >= 0) {
                                  updateBrandField(activeCategoryName, actualIdx, 'closingBalance', parsed);
                                }
                              }
                            }}
                            placeholder="0"
                            className={`w-24 text-right excel-input text-gray-900 dark:text-slate-100 border-2 rounded-lg py-2 px-3 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/15 transition-all text-sm font-bold font-mono shadow-sm hover:border-indigo-300 ${brand.isClosingModified ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20 focus:bg-white' : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 focus:bg-white'}`}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onKeyDown={(e) => handleInputKeyDown(e, activeCategoryName, actualIdx, 'closingBalance', 'sellingPrice')}
                            onWheel={(e) => e.preventDefault()}
                          />
                        )}
                      </td>

                      {/* Selling Price */}
                      <td className="px-4 py-3.5 text-right border-b border-slate-100 text-sm">
                        {isViewMode ? (
                          <div className="text-right font-semibold text-slate-700 font-mono pr-2.5">
                            ₹{(brand.sellingPrice || 0).toFixed(2)}
                          </div>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={brand.sellingPrice === 0 || brand.sellingPrice === null || brand.sellingPrice === undefined ? '' : brand.sellingPrice}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val.replace(/[^0-9]/g, '').length > 7) return;
                              if (val === '') {
                                updateBrandField(activeCategoryName, actualIdx, 'sellingPrice', '');
                              } else {
                                const parsed = parseFloat(val);
                                if (!Number.isNaN(parsed) && parsed >= 0) {
                                  updateBrandField(activeCategoryName, actualIdx, 'sellingPrice', parsed);
                                }
                              }
                            }}
                            placeholder="0.00"
                            className="w-28 text-right excel-input text-gray-900 dark:text-slate-100 border border-slate-200 rounded-lg py-2 px-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all text-sm font-semibold font-mono shadow-sm"
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onKeyDown={(e) => handleInputKeyDown(e, activeCategoryName, actualIdx, 'sellingPrice')}
                            onWheel={(e) => e.preventDefault()}
                          />
                        )}
                      </td>

                      {/* Sales Income */}
                      <td className="px-4 py-3.5 text-right border-b border-slate-100 text-sm text-emerald-600 dark:text-emerald-400 font-bold font-mono pr-2.5">
                        ₹{(brand.salesIncome ?? 0).toFixed(2)}
                      </td>

                      {/* Actions */}
                      {enableStockCheckBrandDeletion && !isViewMode && (currentRecordId ? hasPermission('stock_check', 'edit') : hasPermission('stock_check', 'create')) && (
                        <td className="px-4 py-3.5 text-center border-b border-slate-100 text-sm w-[80px]">
                          <button 
                            onClick={() => removeBrand(activeCategoryName, actualIdx)} 
                            className="w-8 h-8 rounded-lg bg-rose-50 text-rose-500 hover:bg-rose-500 hover:text-white flex items-center justify-center transition-all duration-150 border border-rose-100 hover:border-transparent shadow-sm mx-auto"
                            aria-label="Remove brand"
                            title="Remove Brand"
                          >
                            <i className="fas fa-trash text-xs" aria-hidden="true"></i>
                          </button>
                        </td>
                      )}
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            )}
          </div>

          {/* Brand Cards - phone/small-tablet only */}
          <div className="md:hidden space-y-3">
            {templateLoading ? (
              <div className="text-center py-12 text-slate-500 font-medium flex flex-col items-center gap-3">
                <i className="fas fa-spinner fa-spin text-2xl text-primary"></i>
                <span>Loading category templates...</span>
              </div>
            ) : filteredBrands.length === 0 ? (
              <div className="flex flex-col items-center gap-2 text-center py-12 text-slate-400 text-sm font-medium">
                <i className="fas fa-clipboard-list text-2xl text-slate-300"></i>
                <span>{searchTerm ? 'No brands match your search.' : 'No brands found. Use "+ Add Brand" to setup products.'}</span>
              </div>
            ) : (
              filteredBrands.map((brand, idx) => {
                const originalIdx = currentBrands.findIndex(b => {
                  if ((b.brandId || b.id) && (brand.brandId || brand.id)) {
                    return (b.brandId || b.id) === (brand.brandId || brand.id);
                  }
                  return b.name === brand.name && (b.size || '') === (brand.size || '');
                });
                const isHighlighted = searchTerm && brand.name.toLowerCase().includes(searchTerm.toLowerCase());
                const isBrandFromModal = !!(brand.brandId || brand.id);
                const isReadOnly = isViewMode || isBrandFromModal;
                const actualIdx = originalIdx >= 0 ? originalIdx : idx;
                const canEdit = !isViewMode && (currentRecordId ? hasPermission('stock_check', 'edit') : hasPermission('stock_check', 'create'));

                return (
                  <div
                    key={idx}
                    className={`rounded-xl border p-4 space-y-3 ${isHighlighted ? 'border-amber-300 bg-amber-50/60 dark:bg-amber-500/10' : 'border-slate-200 bg-white dark:bg-slate-800'}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="inline-flex justify-center items-center w-6 h-6 mt-1 rounded-lg bg-slate-100 text-[11px] font-bold text-slate-500 font-mono shrink-0">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0 space-y-2">
                        {isReadOnly ? (
                          <div className="font-semibold text-slate-800 text-sm break-words leading-tight">{brand.name.replace(/\s*-\s*(BTL|TPACK|CAN)$/i, '')}</div>
                        ) : (
                          <input
                            type="text"
                            value={brand.name || ''}
                            onChange={(e) => updateBrandField(activeCategoryName, actualIdx, 'name', e.target.value)}
                            className="w-full excel-input text-gray-900 dark:text-slate-100 border border-slate-200 rounded-lg py-2.5 px-3 focus:outline-none focus:border-primary text-sm font-semibold min-h-[40px]"
                            placeholder="Brand name"
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onWheel={(e) => e.preventDefault()}
                          />
                        )}
                        {isReadOnly ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-bold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 border border-indigo-100 font-mono whitespace-nowrap">
                            {brand.size || '750ml'}
                          </span>
                        ) : (
                          <input
                            type="text"
                            value={brand.size || ''}
                            onChange={(e) => updateBrandField(activeCategoryName, actualIdx, 'size', e.target.value)}
                            className="w-24 text-center excel-input text-gray-900 dark:text-slate-100 border border-slate-200 rounded-lg py-2 px-2 focus:outline-none focus:border-primary text-xs font-bold font-mono min-h-[40px]"
                            placeholder="ML/LTR"
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onWheel={(e) => e.preventDefault()}
                          />
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2.5">
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-500 mb-1">Opening</label>
                        {isViewMode ? (
                          <div className="text-right font-semibold text-slate-700 font-mono px-3 py-2.5 bg-slate-50 rounded-lg">{brand.openingBalance || 0}</div>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            value={brand.openingBalance === 0 || brand.openingBalance === null || brand.openingBalance === undefined ? '' : brand.openingBalance}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val.replace(/[^0-9]/g, '').length > 7) return;
                              if (val === '' || val === '-') {
                                updateBrandField(activeCategoryName, actualIdx, 'openingBalance', val === '-' ? '' : val);
                              } else {
                                const parsed = parseInt(val, 10);
                                if (!Number.isNaN(parsed) && parsed >= 0) {
                                  updateBrandField(activeCategoryName, actualIdx, 'openingBalance', parsed);
                                }
                              }
                            }}
                            placeholder="0"
                            className="w-full text-right excel-input text-gray-900 dark:text-slate-100 border border-slate-200 rounded-lg py-2.5 px-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all text-sm font-semibold font-mono shadow-sm min-h-[40px]"
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onWheel={(e) => e.preventDefault()}
                          />
                        )}
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-slate-500 mb-1">Purchase</label>
                        {isViewMode ? (
                          <div className="text-right font-semibold text-slate-700 font-mono px-3 py-2.5 bg-slate-50 rounded-lg">{brand.purchase || 0}</div>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            value={brand.purchase === 0 || brand.purchase === null || brand.purchase === undefined ? '' : brand.purchase}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val.replace(/[^0-9]/g, '').length > 7) return;
                              if (val === '' || val === '-') {
                                updateBrandField(activeCategoryName, actualIdx, 'purchase', val === '-' ? '' : val);
                              } else {
                                const parsed = parseInt(val, 10);
                                if (!Number.isNaN(parsed) && parsed >= 0) {
                                  updateBrandField(activeCategoryName, actualIdx, 'purchase', parsed);
                                }
                              }
                            }}
                            placeholder="0"
                            className="w-full text-right excel-input text-gray-900 dark:text-slate-100 border border-slate-200 rounded-lg py-2.5 px-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all text-sm font-semibold font-mono shadow-sm min-h-[40px]"
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onWheel={(e) => e.preventDefault()}
                          />
                        )}
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-slate-500 mb-1">Closing</label>
                        {isViewMode ? (
                          <div className="inline-flex items-center justify-end w-full font-bold text-slate-900 font-mono bg-slate-100/60 px-3 py-2.5 rounded-lg border border-slate-200/50">{brand.closingBalance || 0}</div>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            value={brand.closingBalance === 0 || brand.closingBalance === null || brand.closingBalance === undefined ? '' : brand.closingBalance}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val.replace(/[^0-9]/g, '').length > 7) return;
                              if (val === '' || val === '-') {
                                updateBrandField(activeCategoryName, actualIdx, 'closingBalance', val === '-' ? '' : val);
                              } else {
                                const parsed = parseInt(val, 10);
                                if (!Number.isNaN(parsed) && parsed >= 0) {
                                  updateBrandField(activeCategoryName, actualIdx, 'closingBalance', parsed);
                                }
                              }
                            }}
                            placeholder="0"
                            className={`w-full text-right excel-input text-gray-900 dark:text-slate-100 border-2 rounded-lg py-2.5 px-3 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/15 transition-all text-sm font-bold font-mono shadow-sm hover:border-indigo-300 min-h-[40px] ${brand.isClosingModified ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20 focus:bg-white' : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 focus:bg-white'}`}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onWheel={(e) => e.preventDefault()}
                          />
                        )}
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-slate-500 mb-1">Selling Price</label>
                        {isViewMode ? (
                          <div className="text-right font-semibold text-slate-700 font-mono px-3 py-2.5 bg-slate-50 rounded-lg">₹{(brand.sellingPrice || 0).toFixed(2)}</div>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={brand.sellingPrice === 0 || brand.sellingPrice === null || brand.sellingPrice === undefined ? '' : brand.sellingPrice}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val.replace(/[^0-9]/g, '').length > 7) return;
                              if (val === '') {
                                updateBrandField(activeCategoryName, actualIdx, 'sellingPrice', '');
                              } else {
                                const parsed = parseFloat(val);
                                if (!Number.isNaN(parsed) && parsed >= 0) {
                                  updateBrandField(activeCategoryName, actualIdx, 'sellingPrice', parsed);
                                }
                              }
                            }}
                            placeholder="0.00"
                            className="w-full text-right excel-input text-gray-900 dark:text-slate-100 border border-slate-200 rounded-lg py-2.5 px-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all text-sm font-semibold font-mono shadow-sm min-h-[40px]"
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onWheel={(e) => e.preventDefault()}
                          />
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100 text-sm">
                      <span className="text-slate-500">Total: <span className="font-bold text-slate-800 font-mono">{brand.total ?? 0}</span></span>
                      <span className="text-slate-500">Sales: <span className="font-semibold text-slate-700 font-mono">{brand.sales ?? 0}</span></span>
                      <span className="text-emerald-600 dark:text-emerald-400 font-bold font-mono">₹{(brand.salesIncome ?? 0).toFixed(2)}</span>
                    </div>

                    {enableStockCheckBrandDeletion && canEdit && (
                      <button
                        onClick={() => removeBrand(activeCategoryName, actualIdx)}
                        className="w-full h-11 rounded-lg bg-rose-50 text-rose-500 hover:bg-rose-500 hover:text-white flex items-center justify-center gap-2 transition-all duration-150 border border-rose-100 hover:border-transparent shadow-sm font-semibold text-sm"
                        aria-label="Remove brand"
                      >
                        <i className="fas fa-trash text-xs" aria-hidden="true"></i> Remove Brand
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Sales Income Total & Custom Footer */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-5 border-t border-slate-100 bg-slate-50/50 -mx-6 -mb-6 p-6">
            <div className="text-xs text-slate-500 flex items-center gap-1.5 order-2 sm:order-1">
              <i className="fas fa-info-circle text-slate-400"></i>
              <span>
                {isViewMode
                  ? 'Viewing saved stock check data. Click any category tab to inspect detail records.'
                  : 'Totals are calculated in real-time. Navigate tabs to enter quantities for other categories.'}
              </span>
            </div>
            
            <div className="flex items-center gap-3 order-1 sm:order-2 self-end sm:self-auto bg-white dark:bg-slate-800 px-4 py-2.5 rounded-xl border border-slate-200/60 shadow-sm shrink-0">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Sales Income:</span>
              <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400 font-mono">
                ₹{getCategorySalesIncome(activeCategoryName || '')?.toFixed(2) ?? '0.00'}
              </span>
            </div>
          </div>
      </Card>

      {/* Add Brand Modal */}
      {showAddBrandModal && (
        <AddBrandModal
          isOpen={showAddBrandModal}
          onClose={() => setShowAddBrandModal(false)}
          onSuccess={handleBrandsAdded}
          categoryId={activeCategory}
          excludeBrandIds={currentBrands.map(b => b.brandId || b.id).filter(Boolean)}
        />
      )}

      {/* Import Brands Modal */}
      {showImportBrandsModal && (
        <ImportBrandsModal
          isOpen={showImportBrandsModal}
          onClose={() => setShowImportBrandsModal(false)}
          onSuccess={handleBrandsImported}
        />
      )}

      {/* Draft Blocker Modal */}
      <DraftBlockerModal
        isOpen={showDraftBlocker}
        draftToBlock={draftToBlock}
        onDelete={deleteBlockedDraft}
        onContinue={continueBlockedDraft}
      />
    </div>
  );
};

export default StockCheckPage;
